import { z } from "zod"

import { billingPeriodRepresentationSchema } from "./billing-period-representation"

export const serviceTypeSchema = z.enum([`ELECTRICITY`, `SOLAR_PV`])

export const ownershipSchema = z.enum([
  `BEHIND_METER`,
  `COMMUNITY_CHOICE_AGGREGATOR`,
  `COOP`,
  `FEDERAL`,
  `INVESTOR`,
  `MUNI`,
  `POLITICAL_SUBDIVISION`,
  `RETAIL_ENERGY_MARKETER`,
  `STATE`,
  `TRANSMISSION`,
  `UNREGULATED`,
  `WHOLESALE_ENERGY_MARKETER`,
])

export const offeringTypeSchema = z.enum([
  `Bundle`,
  `Bundled`,
  `Delivery`,
  `Energy`,
  `Incentive`,
  `NULL`, // gotta love genability
])

export const loadServingEntitySchema = z.object({
  /**
   * Unique Genability ID (primary key) for each LSE.
   */
  lseId: z.number().positive(),
  /**
   * LSE code (alternate key).
   */
  lseCode: z.string().optional().nullable(),
  /**
   * Published name of the company.
   */
  name: z.string(),
  /**
   * Short code (an alternate key). For US companies, this is the EIA ID.
   */
  code: z.string(),
  /**
   * The URL to the home page of the LSE website.
   */
  websiteHome: z.string().nullable(),
  /**
   * Utility classification based on the type of service they offer.
   * Possible values: Bundled, Delivery, Energy.
   */
  offeringType: offeringTypeSchema.optional().nullable(),
  /**
   * Ownership structure. Possible values include: INVESTOR, COOP, MUNI, FEDERAL,
   * POLITICAL_SUBDIVISION, RETAIL_ENERGY_MARKETER, WHOLESALE_ENERGY_MARKETER,
   * TRANSMISSION, STATE, UNREGULATED.
   */
  ownership: ownershipSchema.optional(),
  /**
   * Service types offered to any customer. Current values include:
   * ELECTRICITY, SOLAR_PV.
   */
  serviceTypes: serviceTypeSchema.nullable().optional(),
  /**
   * Annual total revenue in thousands of local currency (e.g., USD).
   */
  totalRevenues: z.number().nullable().optional(),
  /**
   * Annual total sales in MWh.
   */
  totalSales: z.number().nullable().optional(),
  /**
   * Total customer count.
   */
  totalCustomers: z.number().nullable().optional(),
  /**
   * Service types offered to residential customers. Current values include:
   * ELECTRICITY, SOLAR_PV.
   * Blank means not offered.
   */
  residentialServiceTypes: serviceTypeSchema.nullable().optional(),
  /**
   * Annual residential revenue in thousands of local currency (e.g., USD).
   */
  residentialRevenues: z.number().nullable().optional(),
  /**
   * Annual residential sales in MWh.
   */
  residentialSales: z.number().nullable().optional(),
  /**
   * Residential customer count.
   */
  residentialCustomers: z.number().nullable().optional(),
  /**
   * Service types offered to commercial customers. Current values include:
   * ELECTRICITY, SOLAR_PV.
   * Blank means not offered.
   */
  commercialServiceTypes: z
    .enum([`ELECTRICITY`, `SOLAR_PV`])
    .nullable()
    .optional(),
  /**
   * Annual commercial revenue in thousands of local currency (e.g., USD).
   */
  commercialRevenues: z.number().nullable().optional(),
  /**
   * Annual commercial sales in MWh.
   */
  commercialSales: z.number().nullable().optional(),
  /**
   * Commercial customer count.
   */
  commercialCustomers: z.number().nullable().optional(),
  /**
   * Service types offered to industrial customers. Current values include:
   * ELECTRICITY, SOLAR_PV.
   * Blank means not offered.
   */
  industrialServiceTypes: serviceTypeSchema.nullable().optional(),
  /**
   * Annual industrial revenue in thousands of local currency (e.g., USD).
   */
  industrialRevenues: z.number().nullable().optional(),
  /**
   * Annual industrial sales in MWh.
   */
  industrialSales: z.number().nullable().optional(),
  /**
   * Industrial customer count.
   */
  industrialCustomers: z.number().nullable().optional(),
  /**
   * Service types offered to transportation customers (such as municipal
   * bus services, regional mass transit, etc.). Current values include:
   * ELECTRICITY, SOLAR_PV.
   * Blank means not offered.
   */
  transportationServiceTypes: serviceTypeSchema.nullable().optional(),
  /**
   * Annual transportation revenue in thousands of local currency (e.g., USD).
   */
  transportationRevenues: z.number().nullable().optional(),
  /**
   * Annual transportation sales in MWh.
   */
  transportationSales: z.number().nullable().optional(),
  /**
   * Transportation customer count.
   */
  transportationCustomers: z.number().nullable().optional(),
  /**
   * Details about how the LSE represents billing periods.
   */
  billingPeriodRepresentation: billingPeriodRepresentationSchema.optional(),
})

export type LoadServingEntity = z.infer<typeof loadServingEntitySchema>
